/**
 * Builds a two-level hierarchy: { level_1: { level_2: [assets] } }
 * Assets whose category_code has no matching category entry fall into
 * { 'Not Classified': { <category_code>: [assets] } }
 */
export const buildHierarchy = (assets, categories) => {
  const categoryMap = {};
  (categories || []).forEach((cat) => {
    categoryMap[cat.category_code] = { level_1: cat.level_1, level_2: cat.level_2 };
  });

  const hierarchy = {};
  (assets || []).forEach((asset) => {
    const code = asset.category_code || 'uncategorized';
    const match = categoryMap[code];
    const l1 = match ? match.level_1 : 'Not Classified';
    const l2 = match ? match.level_2 : code;
    if (!hierarchy[l1]) hierarchy[l1] = {};
    if (!hierarchy[l1][l2]) hierarchy[l1][l2] = [];
    hierarchy[l1][l2].push(asset);
  });

  return hierarchy;
};

export const sortedLevel1Keys = (hierarchy) =>
  Object.keys(hierarchy).sort((a, b) => {
    if (a === 'Not Classified') return 1;
    if (b === 'Not Classified') return -1;
    return a.localeCompare(b);
  });

/**
 * Builds level-2-only grouping: { level_2: [assets] } (merge all assets with same level_2 across level_1).
 */
export const buildLevel2Grouping = (hierarchy, sortedLevel1) => {
  const out = {};
  sortedLevel1.forEach((l1) => {
    Object.keys(hierarchy[l1] || {}).forEach((l2) => {
      if (!out[l2]) out[l2] = [];
      out[l2].push(...(hierarchy[l1][l2] || []));
    });
  });
  return out;
};

export const sortedLevel2KeysFromGrouping = (level2Grouping) =>
  Object.keys(level2Grouping).sort((a, b) => {
    if (a === 'Not Classified') return 1;
    if (b === 'Not Classified') return -1;
    return a.localeCompare(b);
  });

/**
 * Assets in hierarchy order (L1 → L2 → assets) for flat list when start level is 3.
 */
export const sortedAssetsFromHierarchy = (hierarchy, sortedLevel1) =>
  sortedLevel1.flatMap((l1) =>
    Object.keys(hierarchy[l1] || {}).sort().flatMap((l2) => hierarchy[l1][l2] || [])
  );

const NO_LOCATION = '—';

/**
 * Same shape as the category hierarchy, with location as L1 and sub-category as L2:
 * { location: { level_2: [assets] } }. Blank location falls into "—".
 */
export const buildLocationHierarchy = (assets, hierarchy) => {
  const level2ByAssetId = {};
  Object.values(hierarchy || {}).forEach((level2Map) => {
    Object.entries(level2Map || {}).forEach(([level2, group]) => {
      (group || []).forEach((asset) => {
        level2ByAssetId[asset.id] = level2;
      });
    });
  });

  const out = {};
  (assets || []).forEach((asset) => {
    const loc = (asset.location_name || '').trim() || NO_LOCATION;
    const level2 = level2ByAssetId[asset.id] || 'Not Classified';
    if (!out[loc]) out[loc] = {};
    if (!out[loc][level2]) out[loc][level2] = [];
    out[loc][level2].push(asset);
  });
  return out;
};

export const sortedLocationKeys = (grouping) =>
  Object.keys(grouping).sort((a, b) => {
    if (a === NO_LOCATION) return 1;
    if (b === NO_LOCATION) return -1;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  });

export const TABLE_START_LEVEL = { L1: 1, L2: 2, L3: 3, LOCATION: 4 };
